/**
 * @file src/utils/classes.js
 *
 * ⚠️ WHY THIS EXISTS.
 *
 * Every styled primitive here takes a `className` and concatenates it onto its
 * own classes:
 *
 *     <span className={`${STYLES[variant]} ${className}`}>
 *
 * That does NOT make the caller's class win. Two Tailwind utilities in the
 * same group have equal specificity, so the winner is whichever appears later
 * in the generated STYLESHEET — the order of the class attribute is irrelevant.
 * The result is a coin flip that reads as intentional code.
 *
 * Found in the wild on the app footer:
 *
 *     class="… uppercase tracking-[0.18em] …  tracking-tight"
 *              ↑ the Text `label` role         ↑ the call site
 *
 *   computed letter-spacing: -0.275px
 *
 * — negative tracking on 11px uppercase text, which is the single worst thing
 * you can do to capitals, arrived at by nobody. 293 <Text> call sites carry a
 * className that re-declares a utility group its variant already sets, so 293
 * of these are decided by stylesheet order today.
 *
 * `mergeClasses` makes the rule explicit and boring: the override wins its own
 * group, the base keeps every group the override does not mention. A class in
 * no known group is never dropped, so an unrecognised utility can only ever be
 * additive.
 *
 * This is deliberately a small, closed implementation rather than a dependency
 * — it needs to cover the utility groups these primitives actually emit, and
 * being able to read the whole rule in one screen is the point.
 */

// The utility groups a primitive and its caller can collide on. Order matters:
// the first match wins, so the narrow patterns (`text-sm`, `text-center`) are
// tested before the catch-all (`text-*` = colour).
const GROUPS = [
  [/^text-(left|center|right|justify|start|end)$/,                          'text-align'],
  [/^text-(xs|sm|base|lg|xl|[2-9]xl)$/,                                     'font-size'],
  [/^text-\[[^\]]*(px|rem|em|pt|ch|vw|vh|%)\]$/,                            'font-size'],
  [/^text-/,                                                                'text-color'],
  [/^font-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/, 'font-weight'],
  [/^font-/,                                                                'font-family'],
  [/^tracking-/,                                                            'tracking'],
  [/^leading-/,                                                             'leading'],
  [/^(block|inline|inline-block|flow-root|contents|hidden|flex|inline-flex|grid|inline-grid|table)$/, 'display'],
  [/^(uppercase|lowercase|capitalize|normal-case)$/,                        'text-transform'],
  [/^(italic|not-italic)$/,                                                 'font-style'],
  [/^(underline|overline|line-through|no-underline)$/,                      'text-decoration'],
  [/^truncate$/,                                                            'truncate'],
  [/^whitespace-/,                                                          'whitespace'],
  [/^(bg-gradient-to-\w+)$/,                                                'bg-image'],
  [/^bg-\[[^\]]*\]$/,                                                       'bg-color'],
  [/^bg-(inherit|current|transparent|black|white|[a-z]+-\d{2,3})(\/\d+)?$/,  'bg-color'],
];

/**
 * The group a single utility belongs to, or null if it belongs to none.
 * Responsive/state prefixes are part of the key — `md:text-sm` may not
 * displace an unprefixed `text-lg`, because they apply at different times.
 */
export function groupOf(cls) {
  const at = cls.lastIndexOf(':');
  const prefix = at === -1 ? '' : cls.slice(0, at + 1);
  const utility = at === -1 ? cls : cls.slice(at + 1);
  // `!important` and negative-value markers are not part of the group.
  const bare = utility.replace(/^!/, '').replace(/^-/, '');
  for (const [pattern, name] of GROUPS) {
    if (pattern.test(bare)) return prefix + name;
  }
  return null;
}

/**
 * Concatenate class strings so that LATER arguments win their own utility
 * group, instead of the winner being decided by stylesheet emission order.
 *
 *   mergeClasses('text-sm text-gray-500', 'text-lg')
 *     → 'text-gray-500 text-lg'          (size replaced, colour kept)
 *
 * Falsy arguments are skipped, so it doubles as a `clsx`-style joiner for the
 * `cond && 'class'` shape.
 */
export function mergeClasses(...parts) {
  const kept = [];   // [{ cls, group }]
  for (const part of parts) {
    if (!part) continue;
    for (const cls of String(part).split(/\s+/)) {
      if (!cls) continue;
      const group = groupOf(cls);
      if (group) {
        // Drop anything already claiming this group — the later one wins.
        for (let i = kept.length - 1; i >= 0; i--) {
          if (kept[i].group === group) kept.splice(i, 1);
        }
      }
      kept.push({ cls, group });
    }
  }
  return kept.map((k) => k.cls).join(' ');
}

export default mergeClasses;
